<script lang="ts">
	import { createEventDispatcher } from "svelte";
	import { tweened } from "svelte/motion";
	import { linear } from "svelte/easing";
	import type { ToastItem } from "./types";

	const DEFAULT_COUNTER = 50;

	export let item: ToastItem;
	const dispatch = createEventDispatcher();
	const progress = tweened(DEFAULT_COUNTER, { duration: item.duration, easing: linear });
	let paused: boolean = false;
	$: customCssClasses = item.cssClasses?.join(" ");

	async function close() {
		if (item.onClose) {
			await item.onClose(item.id);
		}
		dispatch("close", item.id);
	}

	function autoClose() {
		if (($progress === 1 || $progress === 0) && item.autoClose) {
			close();
		}
	}

	if (item.autoClose) {
		progress.set(0).then(autoClose);
	}

	function pause() {
		if (!paused && $progress !== 0 && item.autoClose) {
			progress.set($progress, { duration: 0 });
			paused = true;
		}
	}

	function resume() {
		if (paused && item.autoClose) {
			const duration = (item.duration * $progress) / DEFAULT_COUNTER;
			progress.set(0, { duration }).then(autoClose);
			paused = false;
		}
	}
</script>

<div
	class="pointer-events-auto relative w-full overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 {customCssClasses}"
	role="status"
	on:mouseleave={resume}
	on:mouseenter={pause}>
	<div class="p-4">
		<div class="flex flex-1 items-center">
			<span class="material-icons shrink-0 text-20 font-semibold">
				{#if item.type === "error"}
					cancel
				{:else}
					check_circle
				{/if}
			</span>
			<div class="border-message ml-3 mr-4 flex shrink-0 grow border-r-2 pr-4">
				{@html item.message}
			</div>
			{#if item.canClose}
				<button type="button" class="clickable flex text-current" on:click|stopPropagation={close}>
					<span class="sr-only">Close</span>
					<span class="material-icons text-18">close</span>
				</button>
			{/if}
		</div>
	</div>
	{#if item.autoClose && item.showProgress}
		<progress
			class="absolute bottom-0 left-0 right-0 block h-0.5 w-full appearance-none bg-transparent"
			value={$progress}
			max={DEFAULT_COUNTER} />
	{/if}
</div>

<style>
	progress::-webkit-progress-bar {
		background: transparent;
	}
	progress::-webkit-progress-value {
		background: var(--toastProgressBackground, var(--toastBarBackground, rgba(33, 150, 243, 0.25)));
	}
	progress::-moz-progress-bar {
		background: var(--toastProgressBackground, var(--toastBarBackground, rgba(33, 150, 243, 0.25)));
	}
</style>
